import React from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card, CardBody } from '@/components/ui/Card'

export interface TimeSlotInput {
  start: string
  end: string
}

interface TimeSlotsPickerProps {
  label?: string
  slots: TimeSlotInput[]
  onChange: (slots: TimeSlotInput[]) => void
  error?: string
  maxSlots?: number
}

const TimeSlotsPicker: React.FC<TimeSlotsPickerProps> = ({
  label = 'Proposed Time Slots *',
  slots,
  onChange,
  error,
  maxSlots = 5
}) => {
  const handleAdd = () => {
    if (slots.length < maxSlots) {
      onChange([...slots, { start: '', end: '' }])
    }
  }

  const handleUpdate = (index: number, field: keyof TimeSlotInput, value: string) => {
    const updated = [...slots]
    updated[index] = { ...updated[index], [field]: value }
    onChange(updated)
  }

  const handleRemove = (index: number) => {
    onChange(slots.filter((_, i) => i !== index))
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={handleAdd}
          disabled={slots.length >= maxSlots}
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Slot
        </Button>
      </div>
      {slots.map((slot, index) => (
        <Card key={index} className="mb-2">
          <CardBody className="p-3">
            <div className="flex items-start gap-2">
              <div className="flex-1 grid grid-cols-2 gap-2">
                <input
                  type="datetime-local"
                  value={slot.start}
                  onChange={e => handleUpdate(index, 'start', e.target.value)}
                  className="px-2 py-1 text-sm border border-gray-300 rounded"
                />
                <input
                  type="datetime-local"
                  value={slot.end}
                  onChange={e => handleUpdate(index, 'end', e.target.value)}
                  className="px-2 py-1 text-sm border border-gray-300 rounded"
                />
              </div>
              {slots.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemove(index)}
                  className="text-gray-400 hover:text-red-500"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </CardBody>
        </Card>
      ))}
      {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
    </div>
  )
}

export default TimeSlotsPicker
